import { siteNames } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Actions,
  Bleed,
  ButtonLink,
  IconNewWindow,
  List,
  Stack,
  Text,
  TextLink,
} from 'braid-design-system';
import type { FC } from 'react';

import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { TrackOnMount } from 'src/libs/Analytics';
import { isANZSite } from 'src/utils/site';

import translations from './.vocab';

interface HiringAdviceDialogContentProps {
  link: string;
  onViewMoreButtonClicked: () => void;
  trackHiringAdviceDialogDisplayedEvent: () => void;
  trackJobPlacementHiringAdviceLinkPressedEvent: (
    title:
      | 'How to write a great job ad (with examples)'
      | 'Are candidates seeing your job ad? Make these changes'
      | 'Why your job ad may not be standing out'
      | "Losing candidates through the hiring process? Here's what to do"
      | 'Laws Of Attraction: Find out what New Zealand candidates want'
      | 'Make smarter data-driven decisions with Talent Attraction Lab',
  ) => void;
}

export const HiringAdviceDialogContent: FC<HiringAdviceDialogContentProps> = ({
  link,
  onViewMoreButtonClicked,
  trackHiringAdviceDialogDisplayedEvent,
  trackJobPlacementHiringAdviceLinkPressedEvent,
}) => {
  const { t } = useTranslations(translations);
  const { router, site, language } = useAppConfig();

  const getArticles: () => Array<{
    title:
      | 'How to write a great job ad (with examples)'
      | 'Are candidates seeing your job ad? Make these changes'
      | 'Why your job ad may not be standing out'
      | "Losing candidates through the hiring process? Here's what to do"
      | 'Laws Of Attraction: Find out what New Zealand candidates want'
      | 'Make smarter data-driven decisions with Talent Attraction Lab';
    articleLink: string;
  }> = () => {
    const articles: Array<{
      title:
        | 'How to write a great job ad (with examples)'
        | 'Are candidates seeing your job ad? Make these changes'
        | 'Why your job ad may not be standing out'
        | "Losing candidates through the hiring process? Here's what to do"
        | 'Laws Of Attraction: Find out what New Zealand candidates want'
        | 'Make smarter data-driven decisions with Talent Attraction Lab';
      articleLink: string;
    }> = [];
    if (isANZSite(site)) {
      articles.push(
        {
          title: 'How to write a great job ad (with examples)',
          articleLink: router.hiringAdvice({
            path: 'article/how-to-write-a-great-job-ad-with-examples',
          }),
        },
        {
          title: 'Are candidates seeing your job ad? Make these changes',
          articleLink: router.hiringAdvice({
            path: 'article/are-candidates-seeing-your-job-ad-make-these-changes',
          }),
        },
      );

      if (site === siteNames.employer.seek.nz) {
        articles.push({
          title:
            'Laws Of Attraction: Find out what New Zealand candidates want',
          articleLink: router.loaWithData(),
        });
      }

      return articles;
    }

    articles.push(
      {
        title: 'Why your job ad may not be standing out',
        articleLink: router.marketInsights({
          path: 'article/optimise-your-job-ad-with-unique-market-data',
        }),
      },
      {
        title:
          "Losing candidates through the hiring process? Here's what to do",

        articleLink: router.hiringAdvice({
          path: `article/${
            site === 'employer-seek-id' && language === 'id'
              ? 'kehilangan-kandidat-saat-proses-rekrutmen-inilah-harus-anda-lakukan'
              : 'losing-candidates-through-the-hiring-process-heres-what-to-do-asia'
          }`,
        }),
      },

      {
        title: 'Make smarter data-driven decisions with Talent Attraction Lab',
        articleLink: router.marketInsights({
          path: 'talent-attraction-lab/data-lab',
        }),
      },
    );

    return articles;
  };

  return (
    <TrackOnMount onMount={trackHiringAdviceDialogDisplayedEvent}>
      <Stack space="xlarge">
        <List type="icon" icon={<IconNewWindow />} space="medium">
          {getArticles().map(({ title, articleLink }) => (
            <Bleed left="xxsmall" key={title}>
              <Text>
                <TextLink
                  href={articleLink}
                  weight="weak"
                  target="_blank"
                  onClick={() => {
                    trackJobPlacementHiringAdviceLinkPressedEvent(title);
                  }}
                >
                  {t(title)}
                </TextLink>
              </Text>
            </Bleed>
          ))}
        </List>

        <Stack space="none" align="center">
          <Actions>
            <ButtonLink
              href={link}
              onClick={onViewMoreButtonClicked}
              icon={<IconNewWindow />}
              tone="brandAccent"
              target="_blank"
            >
              {t('View more hiring advice')}
            </ButtonLink>
          </Actions>
        </Stack>
      </Stack>
    </TrackOnMount>
  );
};
